import {
  ArchiveIcon,
  CalendarRangeIcon,
  CrownIcon,
  FolderKanbanIcon,
  ShieldIcon,
  UsersRoundIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, type AvatarSize } from '@/shared/components/avatar';
import type { GroupRole, GroupVisibility } from '@/shared/api/types';
import { useFormatters } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import type { GroupDetails, GroupListItem } from '../api';
import { parseIsoDate, projectPhase, VISIBILITY_ICON } from '../lib/groups';

/** Group/project avatar: the uploaded image or tinted initials, always with rounded corners. */
export function GroupAvatar({
  group,
  size = 'md',
  className,
}: {
  group: { id: string; name: string; avatarUrl: string | null };
  size?: AvatarSize;
  className?: string;
}) {
  return (
    <Avatar
      name={group.name}
      src={group.avatarUrl}
      seed={group.id}
      size={size}
      shape="rounded"
      className={className}
    />
  );
}

/**
 * "Open / Closed / Secret" with its icon; the tooltip explains who sees the group and how people join. Inside links
 * pass `interactive={false}` (no tooltip, not focusable).
 */
export function VisibilityBadge({
  visibility,
  interactive = true,
  className,
}: {
  visibility: GroupVisibility;
  interactive?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('groups');
  const Icon = VISIBILITY_ICON[visibility];
  const badge = (
    <Badge
      variant={visibility === 'open' ? 'secondary' : visibility === 'closed' ? 'info' : 'warning'}
      className={cn('cursor-default', className)}
      data-visibility={visibility}
      tabIndex={interactive ? 0 : undefined}
    >
      <Icon aria-hidden />
      {t(`visibility.${visibility}.label`)}
    </Badge>
  );
  return interactive ? (
    <SimpleTooltip content={t(`visibility.${visibility}.hint`)}>{badge}</SimpleTooltip>
  ) : (
    badge
  );
}

/** "Project" or "Group" with its icon (muted text, for meta rows). */
export function GroupTypeLabel({ isProject, className }: { isProject: boolean; className?: string }) {
  const { t } = useTranslation('groups');
  const Icon = isProject ? FolderKanbanIcon : UsersRoundIcon;
  return (
    <span className={cn('inline-flex items-center gap-1 text-muted-foreground', className)}>
      <Icon className="size-3.5 shrink-0" aria-hidden />
      {isProject ? t('type.project') : t('type.group')}
    </span>
  );
}

const ROLE_ICON = { owner: CrownIcon, moderator: ShieldIcon } as const;

/** Owner / moderator / member badge. */
export function RoleBadge({ role, className }: { role: GroupRole; className?: string }) {
  const { t } = useTranslation('groups');
  const Icon = role === 'member' ? null : ROLE_ICON[role];
  return (
    <Badge
      variant={role === 'owner' ? 'soft' : role === 'moderator' ? 'info' : 'outline'}
      className={className}
      data-role={role}
    >
      {Icon && <Icon aria-hidden />}
      {t(`roles.${role}`)}
    </Badge>
  );
}

export function ArchivedBadge({ className }: { className?: string }) {
  const { t } = useTranslation('groups');
  return (
    <Badge variant="outline" className={cn('text-muted-foreground', className)}>
      <ArchiveIcon aria-hidden />
      {t('archived.badge')}
    </Badge>
  );
}

/**
 * The project's dates ("1 окт. – 31 дек. 2026") with its phase (planned, in progress, overdue, finished); nothing for
 * workgroups or projects without dates.
 */
export function ProjectDates({
  group,
  showPhase = true,
  className,
}: {
  group: Pick<GroupDetails | GroupListItem, 'isProject' | 'projectStart' | 'projectEnd' | 'isArchived'>;
  showPhase?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('groups');
  const fmt = useFormatters();
  if (!group.isProject || (!group.projectStart && !group.projectEnd)) return null;
  const start = group.projectStart ? parseIsoDate(group.projectStart) : null;
  const end = group.projectEnd ? parseIsoDate(group.projectEnd) : null;
  const sameYear = !!start && start.getFullYear() === end?.getFullYear();
  const label =
    start && end
      ? `${fmt.pattern(start, sameYear ? 'd MMM' : 'd MMM yyyy')} – ${fmt.pattern(end, 'd MMM yyyy')}`
      : start
        ? t('dates.from', { date: fmt.pattern(start, 'd MMM yyyy') })
        : t('dates.until', { date: fmt.pattern(end ?? new Date(), 'd MMM yyyy') });
  const phase = projectPhase(group);
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5 text-muted-foreground', className)}>
      <CalendarRangeIcon className="size-3.5 shrink-0" aria-hidden />
      <span className="truncate tabular-nums">{label}</span>
      {showPhase && phase && (
        <span
          data-phase={phase}
          className={cn(
            'shrink-0 rounded-full px-1.5 py-px text-[0.6875rem] font-medium',
            phase === 'active' && 'bg-success-soft text-success-text',
            phase === 'planned' && 'bg-info-soft text-info-text',
            phase === 'overdue' && 'bg-warning-soft text-warning-text',
            phase === 'finished' && 'bg-muted text-muted-foreground',
          )}
        >
          {t(`dates.phase.${phase}`)}
        </span>
      )}
    </span>
  );
}
